import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { authQueryOptions, requireAnonymous } from '@/lib/auth';
import { AuthLayout } from '@/components/AuthLayout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface LoginSearch {
  reset?: string;
  redirectTo?: string;
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const out: LoginSearch = {};
    if (typeof search.reset === 'string') out.reset = search.reset;
    if (typeof search.redirectTo === 'string') out.redirectTo = search.redirectTo;
    return out;
  },
  beforeLoad: ({ context, search }) =>
    requireAnonymous({ context, redirectTo: search.redirectTo }),
  component: LoginPage,
});

const loginSchema = z.object({
  email: z.string().email('Valid email required'),
  password: z.string().min(1, 'Required'),
  rememberMe: z.boolean().default(false),
});
type LoginValues = z.infer<typeof loginSchema>;

function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { reset, redirectTo } = Route.useSearch();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const login = useMutation({
    mutationFn: (values: LoginValues) =>
      api<{ roles?: string[] }>(`/auth/login`, { method: 'POST', json: values }),
    onSuccess: async (user) => {
      // Force the auth query to refetch so guards see the new cookie immediately.
      await qc.invalidateQueries({ queryKey: authQueryOptions.queryKey });
      // A deep link the user was headed to always wins; otherwise admins land in
      // the console by default and taxpayers go to their returns.
      if (redirectTo) {
        navigate({ to: redirectTo as '/returns' });
        return;
      }
      const isAdmin = user?.roles?.includes('Administrator') ?? false;
      navigate({ to: isAdmin ? '/admin' : '/returns' });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const problem = err.problem as { title?: string } | null;
        const title = problem?.title ?? '';
        if (title === 'Two-factor required') {
          navigate({ to: '/login/2fa' });
          return;
        }
        setServerError(title || 'Sign in failed');
      } else {
        setServerError('Could not reach server');
      }
    },
  });

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to continue your return.">
      {reset === 'ok' && (
        <div className="mb-6 flex items-center gap-2 rounded-(--radius) border border-(--color-forest)/25 bg-(--color-forest-soft) px-3.5 py-2.5 text-[13px] font-medium text-(--color-forest)">
          <CheckCircle2 className="size-4 shrink-0" />
          Password reset — sign in with your new password.
        </div>
      )}

      <form
        onSubmit={form.handleSubmit((v) => {
          setServerError(null);
          login.mutate(v);
        })}
        className="space-y-5"
      >
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" placeholder="you@example.com" {...form.register('email')} />
          {form.formState.errors.email && (
            <p className="text-[12px] font-medium text-(--color-danger)">
              {form.formState.errors.email.message}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/forgot-password"
              className="text-[12px] font-semibold text-(--color-navy) hover:underline underline-offset-2"
            >
              Forgot?
            </Link>
          </div>
          <Input id="password" type="password" autoComplete="current-password" placeholder="••••••••" {...form.register('password')} />
          {form.formState.errors.password && (
            <p className="text-[12px] font-medium text-(--color-danger)">
              {form.formState.errors.password.message}
            </p>
          )}
        </div>

        <label className="flex items-center gap-2.5 text-[14px] font-medium text-(--color-ink-2)">
          <input
            type="checkbox"
            className="size-4 rounded border-(--color-border-strong)"
            style={{ accentColor: 'var(--color-navy)' }}
            {...form.register('rememberMe')}
          />
          Remember me on this device
        </label>

        {serverError && (
          <p className="rounded-(--radius) border border-(--color-danger)/25 bg-(--color-danger)/8 px-3.5 py-2.5 text-[13px] font-medium text-(--color-danger)">
            {serverError}
          </p>
        )}

        <Button type="submit" size="lg" className="w-full" disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      <p className="mt-8 text-center text-[14px] text-(--color-ink-muted)">
        New to NightTax?{' '}
        <Link to="/register" className="font-semibold text-(--color-navy) hover:underline underline-offset-2">
          Create an account
        </Link>
        {' · '}
        <Link to="/resend-confirmation" className="font-semibold text-(--color-navy) hover:underline underline-offset-2">
          Resend confirmation
        </Link>
      </p>
    </AuthLayout>
  );
}
